import { useEffect, useState } from "react";
import {
  Box,
  Flex,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Spinner,
  Text,
  useToast,
  SimpleGrid,
  Card,
  CardBody,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  useDisclosure,
} from "@chakra-ui/react";
import { CiSearch } from "react-icons/ci";
import { getAllFeedbacks } from "@/services/feedback.service";
import type { Feedback } from "@/models/feedback.model";
import { useConditionalBackground } from "@/contexts/useConditionalBackground";
import { MdFeedback } from "react-icons/md";
import { ChevronRightIcon, ChevronLeftIcon } from "@chakra-ui/icons";
import { StarIcon } from "@chakra-ui/icons";

export default function FeedbackListPage() {
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [filteredFeedbacks, setFilteredFeedbacks] = useState<Feedback[]>([]);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [search, setSearch] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(
    null
  );
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const backgroundStyle = useConditionalBackground();

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value.toLowerCase();
    setSearch(query);
    setFilteredFeedbacks(
      feedbacks.filter((feedback) =>
        feedback.text.toLowerCase().includes(query)
      )
    );
  };

  const fetchFeedbacks = async () => {
    setIsLoading(true);
    try {
      const { data, totalPages } = await getAllFeedbacks(page, pageSize);
      setFeedbacks(data);
      setFilteredFeedbacks(data);
      setTotalPages(totalPages);
    } catch (error) {
      console.error("Error fetching feedbacks:", error);
      toast({
        title: "Erro ao carregar feedbacks",
        description: "Tente novamente mais tarde.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchFeedbacks();
  }, [page, pageSize]);

  const formatDateTime = (dateTime: Date | undefined) => {
    if (!dateTime) return "N/A";
    const date = new Date(dateTime);
    return `${date.toLocaleDateString("pt-BR")} ${date.toLocaleTimeString(
      "pt-BR",
      {
        hour: "2-digit",
        minute: "2-digit",
      }
    )}`;
  };

  const renderStars = (rating: number, compact = false) => {
    return (
      <HStack spacing={compact ? 0 : 1}>
        {[...Array(5)].map((_, i) => (
          <StarIcon
            key={i}
            color={i < rating ? "yellow.400" : "gray.300"}
            boxSize={compact ? 3 : 5}
          />
        ))}
      </HStack>
    );
  };

  return (
    <Box
      w="100%"
      display="flex"
      flexDirection="column"
      padding={{ base: "2", md: "4" }}
    >
      <Box
        {...backgroundStyle}
        borderRadius="md"
        p={{ base: 4, md: 6 }}
        color="white"
        boxShadow="md"
        w="100%"
      >
        <Flex
          flexDirection={{ base: "column", md: "row" }}
          alignItems={{ base: "flex-start", md: "center" }}
          justifyContent="space-between"
          mb={4}
        >
          <Flex alignItems="center" mb={{ base: 4, md: 0 }}>
            <MdFeedback size="2rem" />
            <Text
              fontSize={{ base: "2xl", md: "4xl" }}
              fontWeight="bold"
              ml={4}
              fontFamily="Lexend Deca"
            >
              Feedbacks
            </Text>
          </Flex>
        </Flex>

        <Flex
          mt={{ base: 4, md: 0 }}
          justifyContent={{ base: "flex-start", md: "flex-start" }}
        >
          <InputGroup width={{ base: "100%", md: "30%" }}>
            <InputLeftElement mt={5} pointerEvents="none">
              <CiSearch color="black" size="2rem" />
            </InputLeftElement>
            <Input
              mt={4}
              placeholder="Pesquisar..."
              variant="filled"
              height={{ base: "56px", md: "48px" }}
              type="text"
              bg={"white"}
              maxLength={30}
              value={search}
              onChange={handleSearch}
              color="black"
              _focus={{ bg: "white", borderColor: "gray.300" }}
              _hover={{ bg: "white" }}
              fontFamily="Lexend Deca"
            />
          </InputGroup>
        </Flex>
      </Box>
      <Flex
        justify="center"
        alignItems="center"
        flexDirection={{ base: "column", md: "row" }}
        width="100%"
        maxWidth="1200px"
        px={{ base: 2, md: 0 }}
        mt={4}
      >
        <Flex alignItems="center" mt={4} mb={{ base: 2, md: 2 }}>
          <IconButton
            icon={<ChevronLeftIcon />}
            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
            isDisabled={page === 1}
            mr={2}
            aria-label="Previous Page"
            size={{ base: "md", md: "sm" }}
          />
          <Text fontSize={{ base: "lg", md: "md" }} fontFamily="Lexend Deca">
            Página {page} de {totalPages}
          </Text>
          <IconButton
            icon={<ChevronRightIcon />}
            onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
            isDisabled={page === totalPages}
            ml={2}
            aria-label="Next Page"
            size={{ base: "md", md: "sm" }}
          />
        </Flex>
        <Box ml={{ base: 0, md: 4 }} mb={2} mt={{ base: 2, md: 4 }}>
          <Select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            width={{ base: "100%", md: "auto" }}
            size={{ base: "md", md: "sm" }}
            fontSize={{ base: "lg", md: "md" }}
            fontFamily="Lexend Deca"
          >
            <option value={10}>10 por página</option>
            <option value={20}>20 por página</option>
            <option value={50}>50 por página</option>
            <option value={100}>100 por página</option>
          </Select>
        </Box>
      </Flex>

      <Box mt={4}>
        {isLoading ? (
          <Flex justifyContent="center" alignItems="center" height="200px">
            <Spinner size="xl" />
          </Flex>
        ) : filteredFeedbacks.length > 0 ? (
          <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={4}>
            {filteredFeedbacks.map((feedback) => (
              <Card
                key={feedback.id}
                onClick={() => {
                  setSelectedFeedback(feedback);
                  onOpen();
                }}
                cursor="pointer"
                height="200px"
                overflow="hidden"
              >
                <CardBody overflowY="auto">
                  <Text fontWeight="bold" mb={2}>
                    {feedback.User?.name}
                  </Text>
                  <Text noOfLines={3}>{feedback.text}</Text>
                  <Flex
                    justifyContent="space-between"
                    alignItems="center"
                    mt={2}
                    position="absolute"
                    bottom="4"
                    left="4"
                    right="4"
                  >
                    {renderStars(feedback.rating, true)}
                    <Text fontSize="sm" color="gray.500">
                      {formatDateTime(feedback.createdAt)}
                    </Text>
                  </Flex>
                </CardBody>
              </Card>
            ))}
          </SimpleGrid>
        ) : (
          <Text
            fontFamily="Lexend Deca"
            color="gray.500"
            textAlign="center"
            mt={8}
          >
            Não há feedbacks no momento.
          </Text>
        )}
      </Box>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Feedback de {selectedFeedback?.User?.name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody maxHeight="60vh" overflowY="auto">
            <Text mb={4}>{selectedFeedback?.text}</Text>
            {renderStars(selectedFeedback?.rating || 0)}
            <Text mt={4} fontSize="sm" color="gray.500">
              Data: {formatDateTime(selectedFeedback?.createdAt)}
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="primary" mr={3} onClick={onClose}>
              Fechar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Flex
        justify="center"
        alignItems="center"
        flexDirection={{ base: "column", md: "row" }}
        width="100%"
        maxWidth="1200px"
        px={{ base: 2, md: 0 }}
        mt={4}
      >
        <Flex alignItems="center" mt={4} mb={{ base: 2, md: 2 }}>
          <IconButton
            icon={<ChevronLeftIcon />}
            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
            isDisabled={page === 1}
            mr={2}
            aria-label="Previous Page"
            size={{ base: "md", md: "sm" }}
          />
          <Text fontSize={{ base: "lg", md: "md" }} fontFamily="Lexend Deca">
            Página {page} de {totalPages}
          </Text>
          <IconButton
            icon={<ChevronRightIcon />}
            onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
            isDisabled={page === totalPages}
            ml={2}
            aria-label="Next Page"
            size={{ base: "md", md: "sm" }}
          />
        </Flex>
        <Box ml={{ base: 0, md: 4 }} mb={2} mt={{ base: 2, md: 4 }}>
          <Select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            width={{ base: "100%", md: "auto" }}
            size={{ base: "md", md: "sm" }}
            fontSize={{ base: "lg", md: "md" }}
            fontFamily="Lexend Deca"
          >
            <option value={10}>10 por página</option>
            <option value={20}>20 por página</option>
            <option value={50}>50 por página</option>
            <option value={100}>100 por página</option>
          </Select>
        </Box>
      </Flex>
    </Box>
  );
}
